import type { AppWorkerClient } from '../../data/AppWorkerClient'
import type {
  QuickBooksArCustomerConcern,
  QuickBooksArTrendAnalysis,
  QuickBooksFpaCachePrepareResponse,
  QuickBooksFpaCacheTablePayload,
  ServerApiClient,
} from '../../data/ServerApiClient'
import { cacheTableSignature, duckDbIdent, previewRowsAsObjects, previewRowValue, sqlString } from './quickBooksGlDetailShared'

export const QBO_AR_ANALYSIS_TABLE = 'fg_qbo_fpa_ar_analysis_table'
const AR_ANALYSIS_ROW_LIMIT = 200_000

type ArArtifactLoadArgs = {
  appApi: ServerApiClient
  workerClient: AppWorkerClient
  connectionId?: string | null
  asOfDate?: string | null
  periodCount?: number
  duckDbOpts?: Record<string, any> | null
  /**
   * Scopes the parsed-analysis cache (e.g. the workbook id). The prepared
   * artifact is materialized into a workbook-scoped DuckDB, so the parsed result
   * must be keyed per workbook to avoid cross-workbook reuse.
   */
  cacheScopeKey?: string | null
  /** Bypass the parsed-analysis cache and re-run the full prepare/parse path. */
  forceRefresh?: boolean
}

// Targeted A/R perf logging. Phases (prepare, materialize, read_rows, parse,
// cache_hit) carry elapsed ms / row count / cache hit so the `/cfo/ar` load can
// be traced without a profiler. Uses Date.now() consistently (never mixes
// performance.now()) per the project's cache-timestamp rule.
const CFO_AR_PERF_PREFIX = '[cfo-ar-perf]'
const AR_PREPARE_TRANSPORT_RETRY_DELAYS_MS = [500, 1_500]
const AR_PREPARE_TRANSPORT_MAX_ATTEMPTS = AR_PREPARE_TRANSPORT_RETRY_DELAYS_MS.length + 1
const AR_DUCKDB_INTERACTIVE_TIMEOUT_MS = 180_000

export function cfoArPerfLog(event: string, payload?: Record<string, any>): void {
  try {
    console.debug(CFO_AR_PERF_PREFIX, event, JSON.stringify(payload || {}))
  } catch {
    /* logging must never throw */
  }
}

async function cfoArPerfStep<T>(event: string, payload: Record<string, any> | undefined, fn: () => Promise<T>): Promise<T> {
  const start = Date.now()
  try {
    const out = await fn()
    cfoArPerfLog(`${event}_done`, {
      ...(payload || {}),
      elapsed_ms: Date.now() - start,
    })
    return out
  } catch (error: any) {
    cfoArPerfLog(`${event}_failed`, {
      ...(payload || {}),
      elapsed_ms: Date.now() - start,
      error: error?.message || String(error),
    })
    throw error
  }
}

const isArPrepareTransportError = (error: any): boolean => {
  if (typeof error?.status === 'number') return false
  const message = String(error?.message || error || '')
    .trim()
    .toLowerCase()
  if (!message) return error?.name === 'TypeError'
  return (
    error?.name === 'TypeError' ||
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('network request failed') ||
    message.includes('load failed') ||
    message.includes('err_http2') ||
    message.includes('protocol_error')
  )
}

const arPrepareTransportMessage =
  'A/R analysis could not reach the artifact prepare service. The A/R artifact was not rejected; the API request failed before FuseGrid could check it. Try again in a moment.'

// Parsed-analysis cache. The same prepared artifact yields a different period
// slice per (asOfMonth, periodCount), so those are part of the key alongside the
// workbook scope and connection. Successful parses only (never null/thrown
// states). In-flight promises are deduped so concurrent loads for the same key
// trigger a single prepare → materialize → read → parse pipeline.
type ParsedArAnalysisCacheEntry = {
  signature: string
  analysis: QuickBooksArTrendAnalysis
  ts: number
}
const PARSED_AR_ANALYSIS_CACHE = new Map<string, ParsedArAnalysisCacheEntry>()
const PARSED_AR_ANALYSIS_INFLIGHT = new Map<string, Promise<QuickBooksArTrendAnalysis>>()
// A short TTL bounds staleness after an in-SPA "Update QuickBooks data" rebuild
// (the module-level Map survives navigation, so without a TTL a rebuilt artifact
// would never be re-read). Within the window, rapid effect re-runs still dedupe
// to a single prepare; after it, the next load re-prepares and the artifact
// signature is compared so an unchanged artifact skips a re-parse. Matches the
// 60s convention used by the CFO bootstrap cache; uses Date.now() throughout.
const PARSED_AR_ANALYSIS_TTL_MS = 60_000

const arAnalysisAsOfMonthKey = (asOfDate?: string | null): string => {
  const monthEnd = monthEndFor(asOfDate)
  return monthEnd ? monthEnd.slice(0, 7) : 'latest'
}

const arAnalysisCacheKey = (parts: Array<string | number | null | undefined>): string => parts.map((part) => String(part ?? '')).join('|')

/**
 * Drop cached parsed A/R analyses. Pass a scope (workbook id) to clear only that
 * workbook's entries; omit to clear everything (used by tests).
 */
export function clearQuickBooksArAnalysisArtifactCache(scopeKey?: string | null): void {
  const scope = trimStr(scopeKey)
  if (!scope) {
    PARSED_AR_ANALYSIS_CACHE.clear()
    PARSED_AR_ANALYSIS_INFLIGHT.clear()
    return
  }
  const prefix = `${scope}|`
  for (const key of [...PARSED_AR_ANALYSIS_CACHE.keys()]) {
    if (key.startsWith(prefix)) PARSED_AR_ANALYSIS_CACHE.delete(key)
  }
  for (const key of [...PARSED_AR_ANALYSIS_INFLIGHT.keys()]) {
    if (key.startsWith(prefix)) PARSED_AR_ANALYSIS_INFLIGHT.delete(key)
  }
}

const trimStr = (value: unknown): string => String(value ?? '').trim()

const rowText = (row: Record<string, any>, ...keys: string[]): string => trimStr(previewRowValue(row, ...keys))

const parseJsonObject = (raw: unknown): any | null => {
  const text = trimStr(raw)
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const monthEndFor = (raw: unknown): string => {
  const text = trimStr(raw).slice(0, 10)
  const match = /^(\d{4})-(\d{2})/.exec(text)
  if (!match) return ''
  const year = Number(match[1])
  const month = Number(match[2])
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return ''
  const end = new Date(Date.UTC(year, month, 0))
  return `${end.getUTCFullYear()}-${String(end.getUTCMonth() + 1).padStart(2, '0')}-${String(end.getUTCDate()).padStart(2, '0')}`
}

const monthEndOffset = (monthEnd: string, offsetMonths: number): string => {
  const match = /^(\d{4})-(\d{2})/.exec(trimStr(monthEnd))
  if (!match) return ''
  const year = Number(match[1])
  const month = Number(match[2])
  if (!Number.isFinite(year) || !Number.isFinite(month)) return ''
  const end = new Date(Date.UTC(year, month - 1 + offsetMonths + 1, 0))
  return `${end.getUTCFullYear()}-${String(end.getUTCMonth() + 1).padStart(2, '0')}-${String(end.getUTCDate()).padStart(2, '0')}`
}

const periodStartKey = (period: Record<string, any>): string => trimStr(period.periodStart ?? period.period ?? '').slice(0, 10)

const periodEndKey = (period: Record<string, any>): string => trimStr(period.periodEnd ?? '').slice(0, 10)

const tableMatches = (table: QuickBooksFpaCacheTablePayload | null | undefined, id: string): boolean => {
  if (!table) return false
  const wanted = trimStr(id)
  return [table.id, table.name]
    .map((value) => trimStr(value))
    .some((value) => value === wanted || value === `qbo_fpa_${wanted}` || value === `fg_qbo_fpa_${wanted}`)
}

export const findQuickBooksArAnalysisTable = (
  cache: QuickBooksFpaCachePrepareResponse | null | undefined,
): QuickBooksFpaCacheTablePayload | null => cache?.tables?.find((table) => tableMatches(table, 'ar_analysis')) || null

const arTableHasData = (table: QuickBooksFpaCacheTablePayload | null | undefined): boolean => {
  if (!table) return false
  if ((table.rows || []).length > 0) return true
  if (Number(table.row_count_hint || 0) > 0) return true
  if (trimStr(table.parquet_url || table.artifact_uri)) return true
  if ((table.parquet_urls || []).some((url) => trimStr(url))) return true
  if ((table.artifact_uris || []).some((url) => trimStr(url))) return true
  return false
}

const arAnalysisParquetUrls = (appApi: ServerApiClient, connectionId: string, table: QuickBooksFpaCacheTablePayload): string[] => {
  const declared = (table.parquet_urls || []).filter((url) => trimStr(url))
  const artifacts = (table.artifact_uris || []).filter((url) => trimStr(url))
  const partCount = declared.length || artifacts.length
  if (connectionId && table.id && typeof appApi.getQuickBooksFpaCacheParquetUrl === 'function') {
    if (partCount > 1) {
      return Array.from({ length: partCount }, (_value, index) => appApi.getQuickBooksFpaCacheParquetUrl(connectionId, table.id, index))
    }
    if (partCount === 1 || table.parquet_url || table.artifact_uri) {
      return [appApi.getQuickBooksFpaCacheParquetUrl(connectionId, table.id)]
    }
  }
  if (declared.length) return declared
  return trimStr(table.parquet_url) ? [trimStr(table.parquet_url)] : []
}

const arTablePartBytes = (table: QuickBooksFpaCacheTablePayload, partCount: number): number[] => {
  const raw = Array.isArray(table.parquet_part_bytes)
    ? table.parquet_part_bytes
    : Array.isArray(table.artifact_part_bytes)
      ? table.artifact_part_bytes
      : []
  return raw
    .slice(0, Math.max(0, partCount))
    .map((value) => Math.max(0, Math.floor(Number(value) || 0)))
    .filter((value) => value > 0)
}

const arTableColumns = (rows: any[]): string[] => {
  const seen = new Set<string>()
  for (const row of rows) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) continue
    Object.keys(row).forEach((key) => {
      const clean = trimStr(key)
      if (clean) seen.add(clean)
    })
  }
  return [...seen]
}

const dropArAnalysisRelation = async (workerClient: AppWorkerClient, duckDbOpts?: Record<string, any> | null) => {
  await workerClient.duckdb_run_sql(`DROP TABLE IF EXISTS ${duckDbIdent(QBO_AR_ANALYSIS_TABLE)};`, {
    ...(duckDbOpts || {}),
    allowMutating: true,
    caller: 'QuickBooksCfoCore:ar_analysis_drop',
    timeoutMs: AR_DUCKDB_INTERACTIVE_TIMEOUT_MS,
    requestPriority: 'cfo_core_visible_route',
    fgLane: 'interactive',
  })
}

async function materializeQuickBooksArAnalysisTable(args: {
  appApi: ServerApiClient
  workerClient: AppWorkerClient
  connectionId: string
  cache: QuickBooksFpaCachePrepareResponse
  duckDbOpts?: Record<string, any> | null
}): Promise<QuickBooksFpaCacheTablePayload> {
  const { appApi, workerClient, connectionId, cache, duckDbOpts } = args
  const table = findQuickBooksArAnalysisTable(cache)
  if (!arTableHasData(table)) {
    throw new Error('QuickBooks prebuilt A/R analysis artifact is not ready. FuseGrid is refreshing CFO analytics artifacts.')
  }
  const concreteTable = table!
  const rows = Array.isArray(concreteTable.rows) ? concreteTable.rows : []
  const urls = arAnalysisParquetUrls(appApi, connectionId, concreteTable)
  await dropArAnalysisRelation(workerClient, duckDbOpts)
  if (urls.length) {
    if (typeof workerClient.duckdb_create_table_from_parquet_urls === 'function') {
      await workerClient.duckdb_create_table_from_parquet_urls(QBO_AR_ANALYSIS_TABLE, urls, {
        ...(duckDbOpts || {}),
        persistent: true,
        caller: 'QuickBooksCfoCore:ar_analysis_parquet',
        requestPriority: 'cfo_core_visible_route',
        fgLane: 'interactive',
        sharedPartCacheKey: [
          connectionId,
          cache.manifest?.cache_dataset_id || '',
          concreteTable.id || concreteTable.name || 'ar_analysis',
          concreteTable.extract_version_id || '',
          concreteTable.row_count_hint ?? rows.length ?? 0,
          urls.length,
          ...arTablePartBytes(concreteTable, urls.length),
        ].join('|'),
        partBytes: arTablePartBytes(concreteTable, urls.length),
        timeoutMs: 900000,
      })
    } else {
      await workerClient.duckdb_run_sql(
        `CREATE OR REPLACE TABLE ${duckDbIdent(QBO_AR_ANALYSIS_TABLE)} AS SELECT * FROM read_parquet(${sqlString(urls[0])})`,
        {
          ...(duckDbOpts || {}),
          allowMutating: true,
          caller: 'QuickBooksCfoCore:ar_analysis_read_parquet',
          requestPriority: 'cfo_core_visible_route',
          fgLane: 'interactive',
          timeoutMs: 180000,
        },
      )
    }
    return concreteTable
  }
  if (!rows.length) {
    throw new Error('QuickBooks prebuilt A/R analysis artifact is empty. FuseGrid is refreshing CFO analytics artifacts.')
  }
  if (typeof workerClient.duckdb_create_temp_table_from_rows !== 'function') {
    throw new Error('Local DuckDB row materialization is not available for A/R analysis.')
  }
  await workerClient.duckdb_create_temp_table_from_rows(QBO_AR_ANALYSIS_TABLE, arTableColumns(rows), rows, {
    ...(duckDbOpts || {}),
    persistent: true,
    caller: 'QuickBooksCfoCore:ar_analysis_rows',
    requestPriority: 'cfo_core_visible_route',
    fgLane: 'interactive',
    dropIfExists: true,
    maxRows: Math.max(1000, rows.length),
    chunkSize: 1000,
    timeoutMs: 120000,
  })
  return concreteTable
}

const readQuickBooksArAnalysisRows = async (
  workerClient: AppWorkerClient,
  rowLimit: number,
  duckDbOpts?: Record<string, any> | null,
  opts?: { asOfDate?: string | null; periodCount?: number },
): Promise<Record<string, any>[]> => {
  const limit = Math.max(1000, Math.min(AR_ANALYSIS_ROW_LIMIT, rowLimit || AR_ANALYSIS_ROW_LIMIT))
  const result = await workerClient.duckdb_run_sql(quickBooksArAnalysisRowsSql(opts), {
    ...(duckDbOpts || {}),
    previewRowLimit: limit,
    caller: 'QuickBooksCfoCore:ar_analysis_read',
    requestPriority: 'cfo_core_visible_route',
    fgLane: 'interactive',
    timeoutMs: 180000,
  })
  const rows = previewRowsAsObjects(result)
  const totalRows = Number((result as any)?.rows ?? rows.length)
  if (Number.isFinite(totalRows) && totalRows > rows.length) {
    throw new Error(
      `QuickBooks prebuilt A/R analysis returned ${rows.length.toLocaleString()} of ${totalRows.toLocaleString()} rows locally.`,
    )
  }
  return rows
}

export function quickBooksArAnalysisRowsSql(opts?: { asOfDate?: string | null; periodCount?: number }): string {
  const targetEnd = monthEndFor(opts?.asOfDate)
  const periodCount = Math.max(0, Math.floor(Number(opts?.periodCount || 0) || 0))
  const startEnd = targetEnd && periodCount > 1 ? monthEndOffset(targetEnd, -periodCount + 1) : targetEnd
  const sectionExpr = `lower(COALESCE(CAST(section AS VARCHAR), ''))`
  const payloadTextExpr = `CAST(payload_json AS VARCHAR)`
  const jsonStringFieldExpr = (field: string) =>
    `NULLIF(regexp_extract(${payloadTextExpr}, ${sqlString(`"${field}"\\s*:\\s*"([^"]+)"`)}, 1), '')`
  const periodDateExpr = `COALESCE(
  TRY_CAST(${jsonStringFieldExpr('periodEnd')} AS DATE),
  TRY_CAST(${jsonStringFieldExpr('period_end')} AS DATE),
  last_day(TRY_CAST(${jsonStringFieldExpr('periodStart')} AS DATE)),
  last_day(TRY_CAST(${jsonStringFieldExpr('period_start')} AS DATE)),
  TRY_CAST(as_of_date AS DATE)
)`
  const boundedWhere =
    targetEnd && startEnd
      ? `WHERE ${sectionExpr} IN (
  'metadata',
  'periods',
  'customer_period_trends',
  'customer_trends',
  'top_customer_concerns',
  'invoice_drilldowns',
  'period_invoice_drilldowns',
  'period_income_account_allocations',
  'collectability',
  'warnings'
)
AND (
  ${sectionExpr} IN ('metadata', 'customer_trends', 'top_customer_concerns', 'collectability', 'warnings')
  OR ${periodDateExpr} BETWEEN CAST(${sqlString(startEnd)} AS DATE) AND CAST(${sqlString(targetEnd)} AS DATE)
)`
      : ''
  return `SELECT * FROM ${duckDbIdent(QBO_AR_ANALYSIS_TABLE)}
${boundedWhere}
ORDER BY ${sectionExpr}, COALESCE(TRY_CAST(ordinal AS INTEGER), 0)`
}

const readQuickBooksArPrebuiltRows = async (
  workerClient: AppWorkerClient,
  duckDbOpts?: Record<string, any> | null,
): Promise<Record<string, any>[]> => {
  const result = await workerClient.duckdb_run_sql(
    `SELECT * FROM ${duckDbIdent(QBO_AR_ANALYSIS_TABLE)}
WHERE lower(COALESCE(CAST(section AS VARCHAR), '')) IN ('duckdb_prebuilt_analysis', 'full_analysis')
ORDER BY CASE lower(COALESCE(CAST(section AS VARCHAR), ''))
  WHEN 'duckdb_prebuilt_analysis' THEN 0
  WHEN 'full_analysis' THEN 1
  ELSE 2
END, COALESCE(TRY_CAST(ordinal AS INTEGER), 0)
LIMIT 5`,
    {
      ...(duckDbOpts || {}),
      previewRowLimit: 5,
      caller: 'QuickBooksCfoCore:ar_analysis_read_prebuilt',
      requestPriority: 'cfo_core_visible_route',
      fgLane: 'interactive',
      timeoutMs: AR_DUCKDB_INTERACTIVE_TIMEOUT_MS,
    },
  )
  return previewRowsAsObjects(result)
}

const readAndParseQuickBooksArAnalysis = async (
  workerClient: AppWorkerClient,
  rowLimit: number,
  opts?: {
    asOfDate?: string | null
    periodCount?: number
    duckDbOpts?: Record<string, any> | null
    key?: string
  },
): Promise<{
  analysis: QuickBooksArTrendAnalysis
  rowsRead: number
  source: 'prebuilt_payload' | 'section_rows'
}> => {
  const prebuiltRows = await cfoArPerfStep('read_prebuilt_rows', { key: opts?.key }, () =>
    readQuickBooksArPrebuiltRows(workerClient, opts?.duckDbOpts),
  )
  if (prebuiltRows.length) {
    const parseStart = Date.now()
    const analysis = parseQuickBooksArAnalysisArtifactRows(prebuiltRows, opts)
    if (analysis) {
      cfoArPerfLog('parse_done', {
        key: opts?.key,
        rows: prebuiltRows.length,
        periods: analysis.periods?.length ?? 0,
        source: 'prebuilt_payload',
        elapsed_ms: Date.now() - parseStart,
      })
      return {
        analysis,
        rowsRead: prebuiltRows.length,
        source: 'prebuilt_payload',
      }
    }
    cfoArPerfLog('parse_prebuilt_failed', {
      key: opts?.key,
      rows: prebuiltRows.length,
      elapsed_ms: Date.now() - parseStart,
    })
  }

  const rows = await cfoArPerfStep('read_rows', { key: opts?.key, row_count_hint: rowLimit }, () =>
    readQuickBooksArAnalysisRows(workerClient, rowLimit, opts?.duckDbOpts, opts),
  )
  cfoArPerfLog('read_rows_result', {
    key: opts?.key,
    rows: rows.length,
    row_count_hint: rowLimit,
  })
  const parseStart = Date.now()
  const analysis = parseQuickBooksArAnalysisArtifactRows(rows, opts)
  if (!analysis) {
    cfoArPerfLog('parse_failed', {
      key: opts?.key,
      rows: rows.length,
      elapsed_ms: Date.now() - parseStart,
    })
    throw new Error(
      'QuickBooks prebuilt A/R analysis artifact is malformed or does not contain the selected period. FuseGrid is refreshing CFO analytics artifacts.',
    )
  }
  cfoArPerfLog('parse_done', {
    key: opts?.key,
    rows: rows.length,
    periods: analysis.periods?.length ?? 0,
    source: 'section_rows',
    elapsed_ms: Date.now() - parseStart,
  })
  return { analysis, rowsRead: rows.length, source: 'section_rows' }
}

const prepareQuickBooksArAnalysisCache = async (
  appApi: ServerApiClient,
  connectionId: string,
  perfPayload: Record<string, any>,
): Promise<QuickBooksFpaCachePrepareResponse> => {
  let lastError: any = null
  for (let attempt = 1; attempt <= AR_PREPARE_TRANSPORT_MAX_ATTEMPTS; attempt += 1) {
    try {
      return await cfoArPerfStep('prepare', { ...perfPayload, attempt }, () =>
        appApi.prepareQuickBooksFpaCache(connectionId, {
          includeAdjustments: false,
          includeArAnalysis: true,
          artifactOnly: true,
        }),
      )
    } catch (error: any) {
      lastError = error
      if (!isArPrepareTransportError(error) || attempt >= AR_PREPARE_TRANSPORT_MAX_ATTEMPTS) break
      cfoArPerfLog('prepare_retry', {
        ...perfPayload,
        attempt,
        next_attempt: attempt + 1,
        delay_ms: AR_PREPARE_TRANSPORT_RETRY_DELAYS_MS[attempt - 1] || 0,
        error: error?.message || String(error),
      })
      const delayMs = AR_PREPARE_TRANSPORT_RETRY_DELAYS_MS[attempt - 1] || 0
      if (delayMs > 0) await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }
  if (isArPrepareTransportError(lastError)) throw new Error(arPrepareTransportMessage)
  throw lastError
}

export function parseQuickBooksArAnalysisArtifactRows(
  rows: any[],
  opts?: { asOfDate?: string | null; periodCount?: number },
): QuickBooksArTrendAnalysis | null {
  const rawRows = Array.isArray(rows) ? rows : []
  const rowSection = (row: Record<string, any>) => rowText(row, 'section', 'Section').toLowerCase()
  const rowPayload = (row: Record<string, any>) => rowText(row, 'payload_json', 'payloadJson', 'PayloadJson')
  const summaryRow = rawRows.find((row) => rowSection(row) === 'duckdb_prebuilt_analysis')
  const fullPayloadRow = rawRows.find((row) => rowSection(row) === 'full_analysis')
  const rawPayload = rowPayload(summaryRow || fullPayloadRow || {})
  let analysis: QuickBooksArTrendAnalysis | null = null
  if (summaryRow && rawPayload) {
    const summary = parseJsonObject(rawPayload)
    if (!summary) return null
    const metadata = summary?.metadata && typeof summary.metadata === 'object' ? summary.metadata : {}
    analysis = {
      asOfDate: trimStr(metadata.asOfDate || summary.asOfDate),
      periodGrain: 'month',
      topCustomerLimit: Number(summary.topCustomerLimit || metadata.topCustomerLimit || 20) || 20,
      periods: Array.isArray(summary.periods) ? summary.periods : [],
      customerPeriodTrends:
        summary.customerPeriodTrends && typeof summary.customerPeriodTrends === 'object' ? summary.customerPeriodTrends : {},
      customerTrends: Array.isArray(summary.customerTrends) ? summary.customerTrends : [],
      topCustomerConcerns: Array.isArray(summary.topCustomerConcerns) ? summary.topCustomerConcerns : [],
      invoiceDrilldowns: Array.isArray(summary.invoiceDrilldowns) ? summary.invoiceDrilldowns : [],
      periodInvoiceDrilldowns: Array.isArray(summary.periodInvoiceDrilldowns) ? summary.periodInvoiceDrilldowns : [],
      periodIncomeAccountAllocations: Array.isArray(summary.periodIncomeAccountAllocations) ? summary.periodIncomeAccountAllocations : [],
      collectability: summary.collectability || null,
      warnings: Array.isArray(summary.warnings) ? summary.warnings : [],
      methodology: summary.methodology || null,
    }
  } else if (rawPayload) {
    const parsed = parseJsonObject(rawPayload)
    if (!parsed) return null
    analysis = parsed
  } else {
    const metadataRow = rawRows.find((row) => rowSection(row) === 'metadata')
    const metadataPayload = parseJsonObject(rowPayload(metadataRow || {}))
    const sectionItems = (sectionName: string) =>
      rawRows
        .filter((row) => rowSection(row) === sectionName)
        .sort((a, b) => Number(previewRowValue(a, 'ordinal', 'Ordinal') || 0) - Number(previewRowValue(b, 'ordinal', 'Ordinal') || 0))
        .map((row) => parseJsonObject(rowPayload(row)))
        .filter((item): item is Record<string, any> => Boolean(item))
    const customerPeriodTrends: NonNullable<QuickBooksArTrendAnalysis['customerPeriodTrends']> = {}
    for (const item of sectionItems('customer_period_trends')) {
      const customerId = trimStr(item.customerId ?? item.customer_id ?? item.customer)
      if (!customerId) continue
      customerPeriodTrends[customerId] = [...(customerPeriodTrends[customerId] || []), item as any]
    }
    analysis = {
      asOfDate: trimStr(metadataPayload?.asOfDate || previewRowValue(metadataRow || {}, 'as_of_date', 'asOfDate')),
      periodGrain: 'month',
      topCustomerLimit: Number(metadataPayload?.topCustomerLimit || 20) || 20,
      periods: sectionItems('periods') as any,
      customerPeriodTrends,
      customerTrends: sectionItems('customer_trends') as any,
      topCustomerConcerns: sectionItems('top_customer_concerns') as any,
      invoiceDrilldowns: sectionItems('invoice_drilldowns') as any,
      periodInvoiceDrilldowns: sectionItems('period_invoice_drilldowns') as any,
      periodIncomeAccountAllocations: sectionItems('period_income_account_allocations') as any,
      collectability: (sectionItems('collectability')[0] || null) as any,
      warnings: rawRows
        .filter((row) => rowSection(row) === 'warnings')
        .map((row) => {
          const parsed = parseJsonObject(rowPayload(row))
          return typeof parsed === 'string' ? parsed : rowPayload(row)
        })
        .filter(Boolean),
    }
  }

  if (!analysis || !Array.isArray(analysis.periods) || !analysis.periods.length) return null
  const requestedAsOf = monthEndFor(opts?.asOfDate)
  const normalizedPeriods = analysis.periods
    .filter((period) => periodEndKey(period as any))
    .sort((a, b) => periodEndKey(a as any).localeCompare(periodEndKey(b as any)))
  if (!normalizedPeriods.length) return null
  const targetPeriodIndex = requestedAsOf
    ? normalizedPeriods.reduce((found, period, index) => (periodEndKey(period as any) <= requestedAsOf ? index : found), -1)
    : normalizedPeriods.length - 1
  if (targetPeriodIndex < 0) return null
  const periodCount = Math.max(0, Math.floor(Number(opts?.periodCount || 0) || 0))
  const periodStartIndex = periodCount > 0 ? Math.max(0, targetPeriodIndex - periodCount + 1) : 0
  const periods = normalizedPeriods.slice(periodStartIndex, targetPeriodIndex + 1)
  const latestPeriodEnd = periodEndKey(periods[periods.length - 1] as any) || trimStr(analysis.asOfDate)
  const selectedStarts = new Set(periods.map((period) => periodStartKey(period as any)).filter(Boolean))
  const selectedEnds = new Set(periods.map((period) => periodEndKey(period as any)).filter(Boolean))
  const customerNameById = new Map(
    (Array.isArray(analysis.customerTrends) ? analysis.customerTrends : []).map((customer) => [
      trimStr((customer as any).customerId || (customer as any).customer),
      trimStr((customer as any).customer || (customer as any).customerId) || 'Unassigned customer',
    ]),
  )
  const customerPeriodTrends: NonNullable<QuickBooksArTrendAnalysis['customerPeriodTrends']> = Object.fromEntries(
    Object.entries(analysis.customerPeriodTrends || {})
      .map(([customerId, customerPeriods]) => [
        customerId,
        (Array.isArray(customerPeriods) ? customerPeriods : []).filter((period) => selectedStarts.has(periodStartKey(period as any))),
      ])
      .filter(([, customerPeriods]) => customerPeriods.length > 0),
  )
  const latestCustomerTrends: QuickBooksArCustomerConcern[] = Object.entries(customerPeriodTrends)
    .map(([customerId, customerPeriods]) => {
      const latest =
        customerPeriods.find((period) => periodEndKey(period as any) === latestPeriodEnd) || customerPeriods[customerPeriods.length - 1]
      const endingAr = Number((latest as any)?.endingAr || 0)
      const overdueAr = Number((latest as any)?.overdueAr || 0)
      const over60Ar = Number((latest as any)?.over60Ar || 0)
      const weightedAverageAge = (latest as any)?.weightedAverageAge == null ? null : Number((latest as any).weightedAverageAge || 0)
      return {
        customerId,
        customer:
          customerNameById.get(customerId) ||
          trimStr((latest as any)?.customer || (latest as any)?.customerName) ||
          customerId ||
          'Unassigned customer',
        endingAr,
        overdueAr,
        over60Ar,
        over90Ar: Number((latest as any)?.over90Ar || 0),
        dso: (latest as any)?.dso == null ? null : Number((latest as any).dso || 0),
        weightedAverageAge,
        riskScore: overdueAr + over60Ar * 1.5 + endingAr * Math.min(2, Math.max(0, Number(weightedAverageAge || 0)) / 365),
        openInvoiceCount: (latest as any)?.openInvoiceCount == null ? null : Number((latest as any).openInvoiceCount || 0),
      }
    })
    .sort((a, b) => Number(b.endingAr || 0) - Number(a.endingAr || 0))
  const topCustomerLimit = Math.max(1, Math.min(20, Math.floor(Number(analysis.topCustomerLimit || 5) || 5)))
  const filterInvoiceDrilldowns = (items: QuickBooksArTrendAnalysis['invoiceDrilldowns'] | undefined) =>
    (Array.isArray(items) ? items : []).filter((invoice) => {
      const periodEnd = trimStr((invoice as any).periodEnd).slice(0, 10)
      if (periodEnd) return selectedEnds.has(periodEnd)
      const invoiceDate = trimStr((invoice as any).invoiceDate).slice(0, 10)
      return !latestPeriodEnd || !invoiceDate || invoiceDate <= latestPeriodEnd
    })
  const filterPeriodRows = <T extends Record<string, any>>(items: T[] | undefined): T[] =>
    (Array.isArray(items) ? items : []).filter((row) => {
      const periodEnd = trimStr((row as any).periodEnd).slice(0, 10)
      if (periodEnd) return selectedEnds.has(periodEnd)
      return false
    })
  return {
    ...analysis,
    asOfDate: latestPeriodEnd || analysis.asOfDate,
    periodGrain: analysis.periodGrain || 'month',
    periods,
    customerPeriodTrends,
    customerTrends: latestCustomerTrends.length ? latestCustomerTrends : analysis.customerTrends || [],
    topCustomerConcerns: latestCustomerTrends.length
      ? [...latestCustomerTrends].sort((a, b) => Number(b.riskScore || 0) - Number(a.riskScore || 0)).slice(0, topCustomerLimit)
      : (analysis.topCustomerConcerns || []).slice(0, topCustomerLimit),
    invoiceDrilldowns: filterInvoiceDrilldowns(analysis.invoiceDrilldowns),
    periodInvoiceDrilldowns: filterInvoiceDrilldowns(analysis.periodInvoiceDrilldowns),
    periodIncomeAccountAllocations: filterPeriodRows((analysis as any).periodIncomeAccountAllocations),
    warnings: Array.from(new Set(Array.isArray(analysis.warnings) ? analysis.warnings : [])),
  }
}

export async function loadQuickBooksArAnalysisArtifact(args: ArArtifactLoadArgs): Promise<QuickBooksArTrendAnalysis> {
  const { appApi, workerClient, connectionId, asOfDate, periodCount = 13, duckDbOpts, cacheScopeKey = null, forceRefresh = false } = args
  const resolvedConnectionId = trimStr(connectionId)
  const cacheKey = arAnalysisCacheKey([
    trimStr(cacheScopeKey),
    resolvedConnectionId || 'local-artifact',
    arAnalysisAsOfMonthKey(asOfDate),
    periodCount,
  ])

  if (!forceRefresh) {
    const cached = PARSED_AR_ANALYSIS_CACHE.get(cacheKey)
    if (cached && Date.now() - cached.ts < PARSED_AR_ANALYSIS_TTL_MS) {
      cfoArPerfLog('cache_hit', { key: cacheKey, signature: cached.signature })
      return cached.analysis
    }
    const inflight = PARSED_AR_ANALYSIS_INFLIGHT.get(cacheKey)
    if (inflight) return inflight
  }

  const run = (async (): Promise<QuickBooksArTrendAnalysis> => {
    if (!resolvedConnectionId) {
      const parsed = await cfoArPerfStep('read_local_artifact', { key: cacheKey }, async () => {
        try {
          return await readAndParseQuickBooksArAnalysis(workerClient, AR_ANALYSIS_ROW_LIMIT, {
            asOfDate,
            periodCount,
            duckDbOpts,
            key: cacheKey,
          })
        } catch (error: any) {
          const message = trimStr(error?.message || error)
          if (/malformed|selected period/i.test(message)) throw error
          throw new Error(
            message
              ? `Prepared A/R analysis artifact is not available locally: ${message}`
              : 'Prepared A/R analysis artifact is not available locally.',
          )
        }
      })
      const { analysis, rowsRead, source } = parsed
      cfoArPerfLog('analysis_ready', {
        key: cacheKey,
        rows: rowsRead,
        source: `local_${source}`,
      })
      PARSED_AR_ANALYSIS_CACHE.set(cacheKey, {
        signature: `local:${source}:${rowsRead}`,
        analysis,
        ts: Date.now(),
      })
      return analysis
    }

    const prepared = await prepareQuickBooksArAnalysisCache(appApi, resolvedConnectionId, { key: cacheKey, connection_id: connectionId })
    const table = await cfoArPerfStep('materialize', { key: cacheKey }, () =>
      materializeQuickBooksArAnalysisTable({
        appApi,
        workerClient,
        connectionId: resolvedConnectionId,
        cache: prepared,
        duckDbOpts,
      }),
    )
    const signature = cacheTableSignature(table, 'ar_analysis')
    // A previously-cached entry for this key with a matching artifact signature
    // is still valid (the TTL expired or the in-flight pipeline raced ahead of
    // the cache read above) — refresh its timestamp and reuse it rather than
    // re-reading + re-parsing. A signature change (a rebuild) falls through.
    const existing = PARSED_AR_ANALYSIS_CACHE.get(cacheKey)
    if (!forceRefresh && existing && existing.signature === signature) {
      existing.ts = Date.now()
      cfoArPerfLog('cache_revalidated', { key: cacheKey, signature })
      return existing.analysis
    }
    const rowCountHint = Math.max(0, Number(table.row_count_hint ?? table.rows?.length ?? 0) || 0)
    const { analysis, rowsRead, source } = await readAndParseQuickBooksArAnalysis(workerClient, Math.max(1000, rowCountHint + 1000), {
      asOfDate,
      periodCount,
      duckDbOpts,
      key: cacheKey,
    })
    cfoArPerfLog('analysis_ready', { key: cacheKey, rows: rowsRead, source })
    PARSED_AR_ANALYSIS_CACHE.set(cacheKey, {
      signature,
      analysis,
      ts: Date.now(),
    })
    return analysis
  })()

  PARSED_AR_ANALYSIS_INFLIGHT.set(cacheKey, run)
  try {
    return await run
  } finally {
    PARSED_AR_ANALYSIS_INFLIGHT.delete(cacheKey)
  }
}
